import { ReactFlowProvider } from '@xyflow/react';
import * as React from 'react';
import { useIntl } from '@leagueplatform/locales';
import { NodeNextKeyMap, NodeTypeLabelMap } from '../types';

/**
 * FlowContext provides flow-builder wide configuration/state that is orthogonal to React Flow's own internals.
 *
 * Currently it exposes a map of node type -> label so can derive labels. The provider also wraps children with
 * ReactFlowProvider so consumers only need to mount one provider in higher-level components (e.g. FlowViewer).
 *
 */

export interface FlowNodeContextState {
  nodeTypeLabelMap: NodeTypeLabelMap;
  nodeNextKeyMap: NodeNextKeyMap;
  addStartNode?: boolean;
  addEndNode?: boolean;
}

const DEFAULT_NODE_LABEL_MAP: NodeTypeLabelMap = {
  action: 'ACTION',
  decision: 'DECISION',
};

// Default: nodes use "next" unless overridden by schema mapping
const DEFAULT_NODE_NEXT_KEY_MAP: NodeNextKeyMap = {
  action: 'next',
  decision: 'next',
  start: 'next',
};

const DEFAULT_STATE: FlowNodeContextState = {
  nodeTypeLabelMap: DEFAULT_NODE_LABEL_MAP,
  nodeNextKeyMap: DEFAULT_NODE_NEXT_KEY_MAP,
  addStartNode: false,
  addEndNode: false,
};

export const FlowContext =
  React.createContext<FlowNodeContextState>(DEFAULT_STATE);

export interface FlowProviderProps {
  nodeTypeLabelMap?: NodeTypeLabelMap;
  nodeNextKeyMap?: NodeNextKeyMap;
  addStartNode?: boolean;
  addEndNode?: boolean;
  children: React.ReactNode;
}

export function FlowProvider({
  nodeTypeLabelMap,
  nodeNextKeyMap,
  addStartNode,
  addEndNode,
  children,
}: FlowProviderProps) {
  const { formatMessage } = useIntl();
  const defaultLabels = React.useMemo<NodeTypeLabelMap>(
    () => ({
      action: formatMessage({ id: DEFAULT_NODE_LABEL_MAP.action }),
      decision: formatMessage({ id: DEFAULT_NODE_LABEL_MAP.decision }),
    }),
    [formatMessage],
  );
  const labelMap = nodeTypeLabelMap || defaultLabels;
  const nextKeyMap = nodeNextKeyMap || DEFAULT_NODE_NEXT_KEY_MAP;
  const state = React.useMemo<FlowNodeContextState>(
    () => ({
      nodeTypeLabelMap: labelMap,
      nodeNextKeyMap: nextKeyMap,
      addStartNode: addStartNode ?? false,
      addEndNode: addEndNode ?? false,
    }),
    [labelMap, nextKeyMap, addStartNode, addEndNode],
  );
  return (
    <FlowContext.Provider value={state}>
      <ReactFlowProvider>{children}</ReactFlowProvider>
    </FlowContext.Provider>
  );
}

export const useFlowContext = () => React.useContext(FlowContext);
